import { Link } from "react-router-dom"
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import type { Investor } from "@/lib/types"

const stageColors: Record<string, string> = {
  Lead: "bg-blue-100 text-blue-700",
  Active: "bg-emerald-100 text-emerald-700",
  Portfolio: "bg-purple-100 text-purple-700",
  Passed: "bg-gray-100 text-gray-500",
}

function EngagementBar({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 rounded-full bg-muted">
        <div
          className={cn(
            "h-1.5 rounded-full transition-all",
            score >= 80 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-red-400"
          )}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{score}</span>
    </div>
  )
}

export function InvestorPreview({ investors }: { investors: Investor[] }) {
  return (
    <div className="glass-strong rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground">Top Investors</h3>
        <Link
          to="/dashboard/investors"
          className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        {investors.slice(0, 5).map((investor) => (
          <Link
            key={investor.id}
            href={`/dashboard/investors/${investor.id}`}
            className="flex items-center gap-3 rounded-lg p-2.5 transition-colors hover:bg-accent/50"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
              {investor.name.split(" ").map((n) => n[0]).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{investor.name}</p>
              <p className="text-xs text-muted-foreground">{investor.firm}</p>
            </div>
            <EngagementBar score={investor.engagementScore} />
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                stageColors[investor.stage]
              )}
            >
              {investor.stage}
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
}
